import React from 'react';
import { Grid, Typography, Card, CardContent, CardActions, Button, Box, CircularProgress, Alert } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useBlogList } from '../hooks/useBlogList';
import AdminLink from '../components/admin/AdminLink';

// DEBUG: Verify import
console.log('[BlogList] AdminLink imported:', AdminLink, typeof AdminLink);

// TODO: Add social media share functionality to Share button

const BlogList = () => {
    const { blogs, isLoading, error } = useBlogList();
    const navigate = useNavigate();

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Date not available';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
            });
        } catch {
            return dateString;
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ margin: 2 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    // Get featured post (first featured or first post)
    const featuredPost = blogs.find(post => post.is_featured) || blogs[0];
    const otherPosts = blogs.filter(post => post.id !== featuredPost?.id);
    
    // DEBUG: Log before rendering AdminLink
    console.log('[BlogList] About to render AdminLink, blogs count:', blogs.length);
    
    return (
        <Box sx={{margin: 2}}>
            <Grid container spacing={1} style={{marginTop: 2}} >
                <Grid item xs={0} sm={0} md={2} lg={2} xl={2}/>
                <Grid item container direction={'row'} xs={12} sm={12} md={7} lg={7} xl={7} spacing={1}>
                    <Grid item xs={12} sm={12} md={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant='h3'>Lighthouse Posts</Typography>
                            {/* DEBUG: This AdminLink should always render (no conditional) */}
                            {(() => {
                                console.log('[BlogList] JSX evaluated - About to render AdminLink');
                                return null;
                            })()}
                            <AdminLink to="/admin/blog" variant="outlined" color="primary">
                                Manage Posts
                            </AdminLink>
                            {(() => {
                                console.log('[BlogList] JSX evaluated - After AdminLink');
                                return null;
                            })()}
                        </Box>
                    </Grid>
                    
                    {/* Featured Post */}
                    {featuredPost && (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant='caption' gutterBottom>
                                        {featuredPost.isFeatured ? 'Featured Post' : 'Latest Post'}
                                    </Typography>
                                    <Typography variant='h4'>
                                        {featuredPost.title}
                                    </Typography>
                                    {featuredPost.excerpt && (
                                        <Typography variant='body1' sx={{ mt: 1, mb: 1 }}>
                                            {featuredPost.excerpt}
                                        </Typography>
                                    )}
                                    <Typography variant='body2' color="text.secondary" sx={{ mb: 2 }}>
                                        {featuredPost.publishedDate ? formatDate(featuredPost.published_at) : 'Date not available'}
                                        {featuredPost.readingTime && ` • ${featuredPost.reading_time} min read`}
                                    </Typography>
                                    <CardActions>
                                        <Button 
                                            size='small'
                                            onClick={() => navigate(`/blogentry/${featuredPost.slug || featuredPost._id}`)}
                                        >
                                            READ MORE
                                        </Button>
                                        <Button size='small'>SHARE</Button>
                                    </CardActions>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Other Posts */}
                    {otherPosts.map((blog) => (
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12} key={blog._id}>
                            <Box style={{backgroundColor: 'rgba(255,255,255,0.6)'}} sx={{p:2, border: '1px dashed grey'}}>
                                <Link 
                                    to={`/blogentry/${blog.slug || blog._id}`}
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    <Grid item container spacing={1}>
                                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                            <Typography variant={'h6'}>{blog.title}</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={12} md={6} lg={6} xl={6} style={{marginTop: 7}}>
                                            <Typography variant={'subtitle2'}>
                                                {formatDate(blog.publishedDate)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Link>
                                {blog.excerpt && (
                                    <Typography variant={'subtitle1'} sx={{ mt: 1 }}>
                                        {blog.excerpt}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Grid item xs={0} sm={0} md={2} lg={0} xl={0}/>
            </Grid>
        </Box>
    );
}

export default BlogList;